import React, { PropsWithChildren } from 'react';
import {
    Container,
    Group,
    getClonedChild,
    Box,
    Button,
    Heading,
    Text,
} from '@landr/maestro';
import { Link } from 'react-router-dom';
import { cva } from '@landr/styled-system/css';

const Title: React.FC<PropsWithChildren> = ({ children }) => (
    <Group.Item>
        <Heading as="h2" textStyle="lead" mb="none">
            {children}
        </Heading>
    </Group.Item>
);

Title.displayName = 'EmptyState.Title';

const Content: React.FC<PropsWithChildren> = ({ children }) => (
    <Group.Item>
        <Text color="text.medium" textStyle="body">
            {children}
        </Text>
    </Group.Item>
);

Content.displayName = 'EmptyState.Content';

const Cta: React.FC<
    PropsWithChildren<{ to?: string; onClick?: () => void }>
> = ({ children, to, onClick }) => {
    if (to) {
        return (
            <Group.Item>
                <Button as={Link} to={to} isRounded mt="sm" size="sm">
                    {children}
                </Button>
            </Group.Item>
        );
    }

    if (onClick) {
        return (
            <Group.Item>
                <Button onClick={onClick} isRounded mt={16} size="sm">
                    {children}
                </Button>
            </Group.Item>
        );
    }

    return null;
};

Cta.displayName = 'EmptyState.Cta';

interface EmptyStateSubComponents {
    Title: typeof Title;
    Content: typeof Content;
    Cta: typeof Cta;
}

const containerStyles = cva({
    variants: {
        isCentered: {
            true: {
                textAlign: 'center',
            },
            false: {
                marginLeft: 0,
            },
        },
    },
});

const EmptyState: React.FC<PropsWithChildren<{ isCentered?: boolean }>> &
    EmptyStateSubComponents = ({ isCentered = false, children }) => {
    const childrenArray = React.Children.toArray(children);
    const title = getClonedChild(childrenArray, EmptyState.Title);
    const content = getClonedChild(childrenArray, EmptyState.Content);
    const cta = getClonedChild(childrenArray, EmptyState.Cta);

    return (
        <Box data-testid="empty-state">
            <Group
                direction="vertical"
                marginX={{
                    md: 'jb',
                }}
                marginY="jb"
            >
                <Group.Item>
                    <Container
                        size="lg"
                        className={containerStyles({ isCentered })}
                    >
                        <Group direction="vertical" gap="none">
                            {title}
                            {content}
                            {cta}
                        </Group>
                    </Container>
                </Group.Item>
            </Group>
        </Box>
    );
};

EmptyState.Title = Title;
EmptyState.Content = Content;
EmptyState.Cta = Cta;

export default EmptyState;
